import React, { useCallback, useState } from 'react';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { useMailboxes } from '../../../music24/outreach/hooks/useMailboxes';
import { useOutreachRealtime } from '../../../music24/outreach/hooks/useOutreachRealtime';
import { MailboxGuideModal } from '../../../music24/outreach/mailboxGuide/MailboxGuideModal';
import type { MailboxRow } from '../../../music24/outreach/outreachTypes';
import { MailboxList } from './MailboxList';

/**
 * Settings → Outreach (design outreach-v2 q1 to q4): the Mailboxes card of the workspace. Every
 * member connects with the guide (`MailboxGuideModal`); only the owner pauses, changes or removes a
 * mailbox (the server refuses a member). The rights line at the bottom of the card says so.
 */
const OutreachTabComponent: React.FC = () => {
  const isOwner = useMyTeamRole() === 'OWNER';
  const mailboxes = useMailboxes();
  const [guide, setGuide] = useState<{ email: string; displayName: string } | 'new' | null>(null);
  const openNew = useCallback(() => setGuide('new'), []);
  const changePassword = useCallback(
    (mailbox: MailboxRow) => setGuide({ email: mailbox.address, displayName: mailbox.displayName }),
    []
  );
  useOutreachRealtime();

  if (mailboxes.isLoading) {
    return <LoadingView />;
  }
  if (mailboxes.error !== null) {
    return <ErrorView error={mailboxes.error ?? undefined} retry={mailboxes.retry} />;
  }
  return (
    <div className="d-flex flex-column gap-3" data-testid="outreach-settings">
      <MailboxList
        mailboxes={mailboxes.mailboxes}
        isOwner={isOwner}
        onConnect={openNew}
        onChangePassword={changePassword}
      />
      {guide !== null && (
        <MailboxGuideModal
          accountId={null}
          initial={guide === 'new' ? undefined : guide}
          onClose={() => setGuide(null)}
        />
      )}
    </div>
  );
};

export const OutreachTab = TypedMemo(OutreachTabComponent);
